/**
 * Web Vitals Performance Monitoring
 *
 * Tracks Core Web Vitals metrics and sends them to analytics.
 * Monitors: LCP, FID, CLS, FCP, TTFB, INP
 *
 * @see https://web.dev/vitals/
 */

import { onCLS, onLCP, onFCP, onTTFB, onINP, type Metric } from 'web-vitals';

/**
 * Metric thresholds (Google's recommended values)
 */
export const METRIC_THRESHOLDS = {
  // Largest Contentful Paint (LCP) - measures loading performance
  LCP: {
    good: 2500,      // < 2.5s is good
    needsWork: 4000, // 2.5s - 4s needs improvement
  },
  // Cumulative Layout Shift (CLS) - measures visual stability
  CLS: {
    good: 0.1,       // < 0.1 is good
    needsWork: 0.25, // 0.1 - 0.25 needs improvement
  },
  // First Contentful Paint (FCP) - measures perceived load speed
  FCP: {
    good: 1800,      // < 1.8s is good
    needsWork: 3000, // 1.8s - 3s needs improvement
  },
  // Time to First Byte (TTFB) - measures server response time
  TTFB: {
    good: 800,       // < 800ms is good
    needsWork: 1800, // 800ms - 1.8s needs improvement
  },
  // Interaction to Next Paint (INP) - measures responsiveness
  INP: {
    good: 200,       // < 200ms is good
    needsWork: 500,  // 200ms - 500ms needs improvement
  },
} as const;

/**
 * Get rating for a metric value
 */
function getRating(
  name: string,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = METRIC_THRESHOLDS[name as keyof typeof METRIC_THRESHOLDS];
  if (!thresholds) return 'good';

  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsWork) return 'needs-improvement';
  return 'poor';
}

/**
 * Send metric to analytics
 *
 * In production, this would send to:
 * - Google Analytics 4 (recommended)
 * - Custom analytics endpoint
 * - Error tracking (Sentry, etc.)
 */
function sendToAnalytics(metric: Metric) {
  const rating = getRating(metric.name, metric.value);

  // Log to console in development
  if (import.meta.env.DEV) {
    const emoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(
      `[Web Vitals] ${emoji} ${metric.name}:`,
      Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric.name === 'CLS' ? '(x1000)' : 'ms',
      `(${rating})`
    );
  }

  // Send to Google Analytics 4 (if available)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: rating,
      non_interaction: true,
    });
  }

  // Send to custom analytics endpoint (future enhancement)
  // fetch('/api/analytics/web-vitals', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({
  //     name: metric.name,
  //     value: metric.value,
  //     rating,
  //     id: metric.id,
  //     timestamp: Date.now(),
  //   }),
  // });
}

/**
 * Start tracking Web Vitals
 *
 * Call this once in your app's entry point (main.tsx or App.tsx)
 *
 * @example
 * ```tsx
 * import { reportWebVitals } from './utils/webVitals';
 *
 * // In main.tsx or App.tsx
 * reportWebVitals();
 * ```
 */
export function reportWebVitals() {
  // Core Web Vitals
  onLCP(sendToAnalytics);  // Largest Contentful Paint
  onCLS(sendToAnalytics);  // Cumulative Layout Shift
  onINP(sendToAnalytics);  // Interaction to Next Paint (replaces deprecated FID)

  // Other important metrics
  onFCP(sendToAnalytics);  // First Contentful Paint
  onTTFB(sendToAnalytics); // Time to First Byte
}

/**
 * Get current Web Vitals performance score (0-100)
 *
 * This is a simplified scoring system based on Core Web Vitals.
 * In production, use Lighthouse or PageSpeed Insights for accurate scores.
 *
 * @returns Estimated performance score (0-100)
 */
export function getPerformanceScore(): Promise<number> {
  return new Promise((resolve) => {
    let score = 100;
    let metricsCollected = 0;
    const totalMetrics = 3; // LCP, INP, CLS

    const checkComplete = () => {
      metricsCollected++;
      if (metricsCollected === totalMetrics) {
        resolve(Math.max(0, Math.min(100, score)));
      }
    };

    onLCP((metric) => {
      const rating = getRating(metric.name, metric.value);
      if (rating === 'needs-improvement') score -= 15;
      if (rating === 'poor') score -= 30;
      checkComplete();
    });

    onINP((metric) => {
      const rating = getRating(metric.name, metric.value);
      if (rating === 'needs-improvement') score -= 10;
      if (rating === 'poor') score -= 20;
      checkComplete();
    });

    onCLS((metric) => {
      const rating = getRating(metric.name, metric.value);
      if (rating === 'needs-improvement') score -= 10;
      if (rating === 'poor') score -= 20;
      checkComplete();
    });

    // Timeout after 10 seconds
    setTimeout(() => resolve(score), 10000);
  });
}

/**
 * Performance monitoring dashboard data
 *
 * Returns current Web Vitals metrics for display in admin dashboard
 */
export interface WebVitalsReport {
  lcp: { value: number; rating: string } | null;
  cls: { value: number; rating: string } | null;
  fcp: { value: number; rating: string } | null;
  ttfb: { value: number; rating: string } | null;
  inp: { value: number; rating: string } | null;
}

let vitalsReport: WebVitalsReport = {
  lcp: null,
  cls: null,
  fcp: null,
  ttfb: null,
  inp: null,
};

/**
 * Get current Web Vitals report
 *
 * @example
 * ```tsx
 * const report = getWebVitalsReport();
 * console.log('LCP:', report.lcp?.value, 'ms');
 * ```
 */
export function getWebVitalsReport(): WebVitalsReport {
  return { ...vitalsReport };
}

/**
 * Initialize Web Vitals tracking with report collection
 */
export function initWebVitals() {
  onLCP((metric) => {
    vitalsReport.lcp = {
      value: metric.value,
      rating: getRating(metric.name, metric.value),
    };
    sendToAnalytics(metric);
  });

  onCLS((metric) => {
    vitalsReport.cls = {
      value: metric.value,
      rating: getRating(metric.name, metric.value),
    };
    sendToAnalytics(metric);
  });

  onFCP((metric) => {
    vitalsReport.fcp = {
      value: metric.value,
      rating: getRating(metric.name, metric.value),
    };
    sendToAnalytics(metric);
  });

  onTTFB((metric) => {
    vitalsReport.ttfb = {
      value: metric.value,
      rating: getRating(metric.name, metric.value),
    };
    sendToAnalytics(metric);
  });

  onINP((metric) => {
    vitalsReport.inp = {
      value: metric.value,
      rating: getRating(metric.name, metric.value),
    };
    sendToAnalytics(metric);
  });
}
